'use client';

// Connector grants picker for the agent create/edit dialog. Fetches the user's
// actually-connected Composio toolkits (GET /api/connections — the same read the
// /connections page uses) and renders them as toggle chips, so a grant is a
// click on a real connected app rather than a hand-typed slug. A free-text
// "advanced" escape hatch stays for unlisted slugs (a toolkit the user connects
// later, or a slug the catalog read didn't surface).
//
// GRACEFUL DEGRADE: if Composio is disabled, the fetch fails, or the read
// errors, we fall back to the plain full-grants text input (the pre-picker
// behaviour) so the dialog is never blocked.
//
// MCP connectors are deliberately NOT offered here: managed-agent runs do not
// consume user MCP connectors (workflows/chat/workflow.ts scopes an agent's
// toolbelt to Composio grants only — the MCP branch is normal-chat-only), so a
// checkbox would grant nothing. Revisit when agent runs load user MCP tools.
import { useEffect, useMemo, useState } from 'react';
import { CaretDown, PlugsConnected, ShieldCheck, SpinnerGap } from '@phosphor-icons/react';

/** One connected Composio toolkit, joined from the accounts + catalog reads. */
interface ConnectedToolkit {
  slug: string;
  name: string;
  logo: string | null;
  isDisabled: boolean;
}

type PickerState = 'loading' | 'ready' | 'error' | 'disabled';

/** Parse a comma/space-separated slug list into normalized, de-duped slugs. */
function parseSlugs(raw: string): string[] {
  const seen = new Set<string>();
  for (const part of raw.split(/[\s,]+/)) {
    const slug = part.trim().toLowerCase();
    if (slug) seen.add(slug);
  }
  return [...seen];
}

export function ConnectorGrantsPicker({
  open,
  grants,
  onChange,
}: {
  /** Dialog open — used to (re)fetch the connected-app list when it opens. */
  open: boolean;
  grants: string[];
  onChange: (next: string[]) => void;
}) {
  const [state, setState] = useState<PickerState>('loading');
  const [toolkits, setToolkits] = useState<ConnectedToolkit[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Fetch the connected toolkits whenever the dialog opens. On any failure we
  // flip to a state that renders the plain text input, never a hard error.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setState('loading');
    fetch('/api/connections')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((data: {
        composioEnabled?: boolean;
        accounts?: Array<{ toolkitSlug?: string; isDisabled?: boolean }>;
        toolkits?: Array<{ slug?: string; name?: string; logo?: string | null }>;
      }) => {
        if (cancelled) return;
        if (!data.composioEnabled) {
          setState('disabled');
          return;
        }
        const catalog = new Map<string, { name: string; logo: string | null }>();
        for (const t of data.toolkits ?? []) {
          if (t.slug) catalog.set(t.slug, { name: t.name ?? t.slug, logo: t.logo ?? null });
        }
        // Dedup accounts by toolkit slug, preferring an enabled account.
        const bySlug = new Map<string, ConnectedToolkit>();
        for (const a of data.accounts ?? []) {
          const slug = a.toolkitSlug;
          if (!slug) continue;
          const existing = bySlug.get(slug);
          if (existing && !existing.isDisabled) continue;
          const meta = catalog.get(slug);
          bySlug.set(slug, {
            slug,
            name: meta?.name ?? slug,
            logo: meta?.logo ?? null,
            isDisabled: Boolean(a.isDisabled),
          });
        }
        setToolkits([...bySlug.values()].sort((x, y) => x.name.localeCompare(y.name)));
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const connectedSlugs = useMemo(() => new Set(toolkits.map((t) => t.slug)), [toolkits]);
  // Grants that don't correspond to a connected toolkit chip — surfaced in the
  // advanced free-text so they're never silently dropped on save.
  const extraGrants = useMemo(
    () => grants.filter((g) => !connectedSlugs.has(g)),
    [grants, connectedSlugs],
  );

  const toggle = (slug: string) => {
    onChange(grants.includes(slug) ? grants.filter((g) => g !== slug) : [...grants, slug]);
  };

  // Rewrite only the "extra" (unlisted) slugs, preserving toggled chip grants.
  const setExtras = (raw: string) => {
    const kept = grants.filter((g) => connectedSlugs.has(g));
    const next = parseSlugs(raw);
    const merged = new Set([...kept, ...next]);
    onChange([...merged]);
  };

  const header = (
    <div className="flex items-start gap-3">
      <ShieldCheck weight="duotone" className="mt-0.5 size-4 shrink-0 text-sky-400" />
      <div className="flex-1">
        <p className="text-xs font-medium text-foreground/90">Connectors</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Connected apps this agent may use. The agent can only use toolkits you have connected on{' '}
          <a href="/connections" className="underline decoration-dotted underline-offset-2 hover:text-foreground">
            Connections
          </a>
          .
        </p>
      </div>
    </div>
  );

  // Fallback: plain full-grants text input (the pre-picker behaviour). Used when
  // Composio is off or the read failed — never blocks the dialog.
  const fallbackInput = (note: string) => (
    <div className="rounded-lg border border-border/60 px-3 py-2.5">
      {header}
      <p className="mt-2 text-[11px] text-muted-foreground/80">{note}</p>
      <input
        value={grants.join(', ')}
        onChange={(e) => onChange(parseSlugs(e.target.value))}
        placeholder="none"
        className="mt-2 w-full rounded-md border border-input bg-transparent px-3 py-1.5 font-mono text-xs outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
    </div>
  );

  if (state === 'error') {
    return fallbackInput('Couldn’t load your connected apps — enter Composio toolkit slugs, comma-separated (e.g. gmail, slack).');
  }
  if (state === 'disabled') {
    return fallbackInput('Enter Composio toolkit slugs, comma-separated (e.g. gmail, slack). Leave empty for none.');
  }

  return (
    <div className="rounded-lg border border-border/60 px-3 py-2.5">
      {header}

      {state === 'loading' ? (
        <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
          <SpinnerGap className="size-3.5 animate-spin" />
          Loading your connected apps…
        </div>
      ) : toolkits.length === 0 ? (
        <p className="mt-3 text-[11px] text-muted-foreground">
          No connected apps yet.{' '}
          <a href="/connections" className="underline decoration-dotted underline-offset-2 hover:text-foreground">
            Connect an app
          </a>{' '}
          to grant it here, or add a slug below.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {toolkits.map((t) => {
            const on = grants.includes(t.slug);
            return (
              <button
                key={t.slug}
                type="button"
                onClick={() => toggle(t.slug)}
                aria-pressed={on}
                title={t.isDisabled ? `${t.name} (connection disabled)` : t.name}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition-colors ${
                  on
                    ? 'border-sky-400/60 bg-sky-400/15 font-semibold text-foreground'
                    : 'border-border/70 font-medium text-muted-foreground hover:border-border hover:text-foreground'
                }`}
              >
                {t.logo ? (
                  <img src={t.logo} alt="" className="size-3.5 rounded-sm object-contain" />
                ) : (
                  <PlugsConnected weight="duotone" className="size-3.5" />
                )}
                <span className="truncate max-w-[9rem]">{t.name}</span>
                {t.isDisabled && <span className="text-[10px] text-amber-400">off</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Advanced: free-text escape hatch for unlisted slugs. */}
      {state === 'ready' && (
        <div className="mt-3 border-t border-border/40 pt-2.5">
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            aria-expanded={advancedOpen}
            className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
          >
            <CaretDown weight="bold" className={`size-3 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
            Advanced — add an unlisted slug
            {extraGrants.length > 0 && !advancedOpen && (
              <span className="ml-1 rounded-full bg-muted px-1.5 text-[10px] tabular-nums">{extraGrants.length}</span>
            )}
          </button>
          {advancedOpen && (
            <>
              <input
                value={extraGrants.join(', ')}
                onChange={(e) => setExtras(e.target.value)}
                placeholder="e.g. notion, linear"
                className="mt-2 w-full rounded-md border border-input bg-transparent px-3 py-1.5 font-mono text-xs outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              <p className="mt-1 text-[10px] text-muted-foreground/70">
                Composio toolkit slugs, comma-separated. The agent can still only use apps you have connected.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
